import cookieNames from '@/constants/CookieNames';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  split,
} from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import createAuthLink from './apollo-links/authLink';
import createHttpLink from './apollo-links/httpLink';
import createLoggerLink from './apollo-links/loggerLink';
import createRefreshLink from './apollo-links/refreshLink';
import createRetryLink from './apollo-links/retryLink';
import createTimeoutLink from './apollo-links/timeoutLink';
import createWsLink from './apollo-links/wsLink';

export default function createApolloClient({
  // URL to the HTTP API
  httpEndpoint = null,
  // Url to the Websocket API
  wsEndpoint = null,
  // Token used in localstorage
  tokenName = cookieNames.ACCESS_TOKEN,
  // Is currently Server-Side Rendering or not
  ssr = false,
  // Only use Websocket for all requests (including queries and mutations)
  websocketsOnly = false,
  // Default HTTP link if httpEndpoint is not defined
  defaultHttpEndpoint = `${process.env.PUBLICAPI_URL}/graphql`, // 2DO: process.env.DEFAULT_GQL_API
  // Options for the default cache
  inMemoryCacheOptions = {},
  // Additional Apollo client options
  defaultOptions = {},
  // httpOptions for the httpLink (headers, credentials, fetch, fetchOptions)
  httpOptions = {},
  // websocket options
  wsOptions = {},
  // retry options
  retryOptions = {
    delay: {},
    attempts: {},
  },
  // timeout in milliseconds
  timeout,
}) {
  const disableHttp = websocketsOnly && !ssr && wsEndpoint;

  // configure Apollo Memory Cache
  const cache = new InMemoryCache(inMemoryCacheOptions);

  let httpLink = null;
  const uri = httpEndpoint || defaultHttpEndpoint;
  if (!disableHttp) {
    // // create HTTP link
    httpLink = createHttpLink({
      uri,
      httpOptions,
    });
  }

  // create WebSocket Link
  let wsLink = null;
  if (process.browser && !!wsEndpoint) {
    wsLink = createWsLink({
      uri: wsEndpoint,
      wsOptions,
    });
  }

  // create Split Link if wsEndpoint is configured
  const splitLink =
    process.browser && !!wsEndpoint
      ? split(
          ({ query }) => {
            const { kind, operation } = getMainDefinition(query);

            return (
              kind === 'OperationDefinition' && operation === 'subscription'
            );
          },
          wsLink,
          httpLink
        )
      : httpLink;

  // create Authentication Link
  const authLink = createAuthLink(tokenName);
  // create Logger Link
  const loggerLink = createLoggerLink();

  // create Retry link
  const retryLink = createRetryLink(retryOptions);
  // create timeout Link
  const timeoutLink = createTimeoutLink(timeout);

  const refreshLink = createRefreshLink();

  const link = ApolloLink.from([
    loggerLink,
    refreshLink,
    authLink,
    retryLink,
    timeoutLink,
    splitLink,
  ]);

  const client = new ApolloClient({
    connectToDevTools: process.env.NODE_ENV === 'development',
    link,
    cache,
    defaultOptions,
  });

  return client;
}
